import React from 'react';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import useScript from '../../lib/hooks.js';
import config from '../../config';
import { BillingAPI } from '../../api';
import Button from '../Button';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import Loader from './Loader';

const formId = 'CardFormId';
const defaultTokenizeErrorMessage = 'Some payment input fields are invalid.';
const defaultHostedFieldsErrorMessage = 'Unable to initialise card payment gateway';

const removeClassName = (el, className) => {
  el.className = el.className.split(' ').filter(c => c !== className).join(' ');
};

const addClassName = (el, className) => {
  el.className = el.className.split(' ').filter(c => c !== className).join(' ') + ' ' + className;
};

const useStyles = makeStyles({
  form: {
    '& .braintree-hosted-fields-invalid': {
      borderColor: '#dc3545',
      boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)',
    },
    '& .invalid': {
      borderColor: '#dc3545',
      boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)',
    }
  },
  input: {
    height: '2rem',
    border: 'solid 1px #ced4da',
    padding: '.375rem .75rem',
  },
  field: {
    width: '100%',
  },
  fields: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    marginBottom: '2rem',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '1rem',
  },
  alert: {
    marginBottom: '2rem',
  },
  spinner: {
    height: '20rem',
    width: '33rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
});


const CardForm = ({providerUuid, onComplete, onCancel, busy=false, disabled=false}) => {
  const [clientToken, setClientToken] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [formLoaded, setFormLoaded] = useState(false);
  const classes = useStyles();

  useScript(config.settings.BRAINTREE_CLIENT);
  useScript(config.settings.BRAINTREE_HOSTED_FIELDS);

  const braintreeSetup = (clientInstance) => {
    const form = document.getElementById(formId);
    const options = {
      styles: {
        'input': {
          'font-size': '14px',
          'font-family': 'roboto, verdana, sans-serif',
          'font-weight': 'lighter',
          'color': 'black',
          'border': 'solid 1px blue',
        },
        'input.invalid': {
          'color': 'red'
        },
        'input.valid': {
          'color': 'black',
          'border-color': 'red',
        },
        ':focus': {
          'color': 'black',
          'border-color': 'blue',
        },
        '.valid': {
          'color': 'black'
        },
        '.invalid': {
          'color': 'black'
        }
      },
      fields: {
        cardholderName: {
          selector: '#card-name',
          placeholder: 'Cardholder Name',
        },
        number: {
          selector: '#card-number',
          placeholder: '•••• •••• •••• ••••',
        },
        cvv: {
          selector: '#cvv',
          placeholder: '•••',
        },
        expirationDate: {
          selector: '#expiration-date',
          placeholder: 'MM/YY',
        }
      }
    };

    const updateFieldValidity = (name, field) => {
      if (field.isEmpty || field.isValid) {
        removeClassName(field.container, 'invalid');
      } else {
        addClassName(field.container, 'invalid');
      }
    };

    const setupEvents = (hostedFieldsErr, hostedFieldsInstance) => {
      if (hostedFieldsErr) {
        setErrorMessage(defaultHostedFieldsErrorMessage);
        return;
      }

      hostedFieldsInstance.on('validityChange', event => {
        Object.entries(event.fields).map(([key, value]) => updateFieldValidity(key, value));
      });

      form.addEventListener('submit', function (event) {
        event.preventDefault();

        hostedFieldsInstance.tokenize(function (tokenizeErr, payload) {
          if (tokenizeErr) {
            setErrorMessage(defaultTokenizeErrorMessage);
            return;
          }
          onComplete({
            billing_payment_method_nonce: payload.nonce,
          });
        });
      }, false);
      setFormLoaded(true);
    };

    window.braintree.hostedFields.create({
      client: clientInstance,
      ...options,
    }, setupEvents);
  };

  useEffect(() => {
    const getClientToken = async () => {
      try {
        if (providerUuid === null) {
          const response = await BillingAPI.getClientToken({anonymous: false});
          setClientToken(response.payload.client_token);
        } else {
          const response =  await BillingAPI.getProviderClientToken(providerUuid);
          setClientToken(response.payload.client_token);
        }
      } catch {
        setErrorMessage('Unable to initialise payment gateway.');
      }
    };
    getClientToken();
  }, [providerUuid]);

  useEffect(() => {
    if (!clientToken) return;
    window.braintree.client.create({
      authorization: clientToken,
    }, function (clientErr, clientInstance) {
      if (clientErr) {
        setErrorMessage('Unable to establish secure connection.');  // nonsense
        return;
      }
      braintreeSetup(clientInstance);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientToken]);

  return (
    <div>
      {!formLoaded && <div className={classes.spinner}><Loader/></div>}

      <form action="/" method="post" id={formId} className={classes.form} style={{display: formLoaded ? 'block' : 'none'}}>
        <div className={classes.fields}>

          <div className={classes.row}>
            <div className={classes.field}>
              <label htmlFor="card-name">Cardholder Name</label>
              <div id="card-name" className={classes.input}></div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.field}>
              <label htmlFor="card-number">Card Number</label>
              <div id="card-number" className={classes.input}></div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.field}>
              <label htmlFor="expiration-date">Expiration Date</label>
              <div id="expiration-date" className={classes.input}></div>
            </div>
            <div className={classes.field}>
              <label htmlFor="cvv">CVV</label>
              <div id="cvv" className={classes.input}></div>
            </div>
          </div>
        </div>

        {errorMessage &&
          <Alert severity="error" className={classes.alert}>
            {errorMessage}
          </Alert>
        }

        <Button
          variant="outlined"
          color="primary"
          label="Cancel"
          disabled={disabled || busy}
          onClick={onCancel}
          pointer=""
        />

        <Button label="Add" busy={busy} disabled={disabled} onClick={() => document.getElementById(formId).dispatchEvent(new Event('submit'))}/>
      </form>
    </div>

  );
};

CardForm.propTypes = {
  providerUuid: PropTypes.string.isRequired,
  allowDelete: PropTypes.bool,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  buttonLabel: PropTypes.string,
  footer: PropTypes.node,
  busy: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default CardForm;
